import { useState, useEffect, useRef } from "react";
import { Plus, Search, Trash2, Users, UserPlus, Upload, FileText, X, CheckCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import API from "@/lib/api";
import { toast } from "sonner";
import { CUSTOMER } from "@/constants/testIds";

export default function Contacts() {
  const [contacts, setContacts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [contactModal, setContactModal] = useState(false);
  const [groupModal, setGroupModal] = useState(false);
  const [importModal, setImportModal] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", phone: "", email: "", company: "", birthday: "" });
  const [groupForm, setGroupForm] = useState({ name: "", description: "" });
  const [saving, setSaving] = useState(false);

  // CSV import state
  const [csvFile, setCsvFile] = useState(null);
  const [csvGroupId, setCsvGroupId] = useState("");
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => { loadData(); }, [page, search]);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (search) params.append("search", search);
      const [c, g] = await Promise.all([API.get(`/contacts?${params}`), API.get("/contacts/groups")]);
      setContacts(c.data.contacts || []);
      setTotal(c.data.total || 0);
      setGroups(g.data.groups || []);
    } catch { toast.error("Failed to load contacts"); }
    finally { setLoading(false); }
  };

  const saveContact = async () => {
    if (!form.first_name || !form.phone) { toast.error("Name and phone are required"); return; }
    setSaving(true);
    try {
      await API.post("/contacts", form);
      toast.success("Contact added");
      setContactModal(false);
      setForm({ first_name: "", last_name: "", phone: "", email: "", company: "", birthday: "" });
      loadData();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to add contact"); }
    finally { setSaving(false); }
  };

  const saveGroup = async () => {
    if (!groupForm.name) { toast.error("Group name is required"); return; }
    setSaving(true);
    try {
      await API.post("/contacts/groups", groupForm);
      toast.success("Group created");
      setGroupModal(false);
      setGroupForm({ name: "", description: "" });
      loadData();
    } catch { toast.error("Failed to create group"); }
    finally { setSaving(false); }
  };

  const deleteContact = async (id) => {
    if (!window.confirm("Delete this contact?")) return;
    try { await API.delete(`/contacts/${id}`); toast.success("Contact deleted"); loadData(); }
    catch { toast.error("Failed to delete"); }
  };

  const deleteGroup = async (id) => {
    if (!window.confirm("Delete this group?")) return;
    try { await API.delete(`/contacts/groups/${id}`); toast.success("Group deleted"); loadData(); }
    catch { toast.error("Failed to delete group"); }
  };

  const importCsv = async () => {
    if (!csvFile) { toast.error("Select a CSV file first"); return; }
    setImporting(true);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append("file", csvFile);
      if (csvGroupId) formData.append("group_id", csvGroupId);
      const { data } = await API.post("/contacts/import-csv", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportResult(data);
      toast.success(`Imported ${data.added} contacts`);
      loadData();
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); }
    finally { setImporting(false); }
  };

  const resetImport = () => {
    setCsvFile(null);
    setCsvGroupId("");
    setImportResult(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <div data-testid={CUSTOMER.contactsTable} className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900">Contacts</h1>
          <p className="text-sm text-slate-500 mt-1">{total} contacts · {groups.length} groups</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setImportModal(true)} variant="outline" size="sm" className="border-slate-300 text-slate-700 h-9 hidden sm:flex hover:bg-slate-50" data-testid="import-csv-button">
            <Upload size={14} className="mr-2" /> Import CSV
          </Button>
          <Button onClick={() => setGroupModal(true)} variant="outline" size="sm" className="border-slate-300 text-slate-700 h-9 hidden sm:flex hover:bg-slate-50">
            <Users size={14} className="mr-2" /> New Group
          </Button>
          <Button onClick={() => setContactModal(true)} size="sm" className="bg-[#0c1e41] hover:bg-[#091629] h-9 shadow-sm" data-testid={CUSTOMER.addContactButton}>
            <UserPlus size={14} className="mr-2" /> Add Contact
          </Button>
        </div>
      </div>

      <Tabs defaultValue="contacts">
        <TabsList className="bg-slate-100 border border-slate-200 p-1 h-auto">
          <TabsTrigger value="contacts" className="data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm text-slate-600 text-sm">Phonebook ({total})</TabsTrigger>
          <TabsTrigger value="groups" className="data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm text-slate-600 text-sm">Groups ({groups.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="contacts" className="mt-4 space-y-4">
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Search contacts..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} className="pl-9 bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 h-9" />
          </div>
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200 text-xs text-slate-500 uppercase tracking-wider">
                    <th className="text-left px-5 py-3.5 font-semibold">Name</th>
                    <th className="text-left px-5 py-3.5 font-semibold">Phone</th>
                    <th className="text-left px-5 py-3.5 font-semibold hidden sm:table-cell">Email</th>
                    <th className="text-left px-5 py-3.5 font-semibold hidden md:table-cell">Company</th>
                    <th className="text-right px-5 py-3.5 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {loading ? [...Array(5)].map((_, i) => <tr key={i}><td colSpan={5}><div className="h-10 bg-slate-100 animate-pulse mx-5 my-2 rounded" /></td></tr>)
                  : contacts.length === 0 ? <tr><td colSpan={5} className="px-5 py-10 text-center text-slate-400 text-sm">No contacts yet. Add your first contact.</td></tr>
                  : contacts.map((c) => (
                    <tr key={c._id} className="table-row-hover">
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-7 h-7 rounded-full bg-[#dce4f0] flex items-center justify-center text-[#0c1e41] text-xs font-bold shrink-0">
                            {c.first_name?.[0]?.toUpperCase()}
                          </div>
                          <span className="text-sm font-medium text-slate-900">{c.first_name} {c.last_name}</span>
                        </div>
                      </td>
                      <td className="px-5 py-3 font-mono text-sm text-slate-700">{c.phone_normalized || c.phone}</td>
                      <td className="px-5 py-3 hidden sm:table-cell text-sm text-slate-500">{c.email || "—"}</td>
                      <td className="px-5 py-3 hidden md:table-cell text-sm text-slate-500">{c.company || "—"}</td>
                      <td className="px-5 py-3 text-right">
                        <button onClick={() => deleteContact(c._id)} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors">
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {Math.ceil(total / 20) > 1 && (
              <div className="flex items-center justify-between px-5 py-3 border-t border-slate-200">
                <span className="text-xs text-slate-500">Page {page}</span>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="border-slate-300 text-slate-600 h-8 text-xs" disabled={page === 1} onClick={() => setPage(page - 1)}>Prev</Button>
                  <Button size="sm" variant="outline" className="border-slate-300 text-slate-600 h-8 text-xs" disabled={contacts.length < 20} onClick={() => setPage(page + 1)}>Next</Button>
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="groups" className="mt-4">
          <div className="flex justify-end mb-4">
            <Button onClick={() => setGroupModal(true)} size="sm" className="bg-[#0c1e41] hover:bg-[#091629] h-9 shadow-sm">
              <Plus size={14} className="mr-2" /> New Group
            </Button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {groups.length === 0 ? (
              <div className="col-span-3 text-center py-10 text-slate-400 text-sm">No groups yet</div>
            ) : groups.map((g) => (
              <div key={g._id} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm hover:shadow-md transition-all">
                <div className="flex items-start justify-between mb-3">
                  <div className="w-9 h-9 bg-[#eef1f8] border border-[#c0cfe8] rounded-xl flex items-center justify-center">
                    <Users size={17} className="text-[#0c1e41]" />
                  </div>
                  <button onClick={() => deleteGroup(g._id)} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors">
                    <Trash2 size={13} />
                  </button>
                </div>
                <h3 className="text-sm font-semibold text-slate-900">{g.name}</h3>
                {g.description && <p className="text-xs text-slate-500 mt-1">{g.description}</p>}
                <div className="mt-3 text-xs font-mono font-bold text-[#0c1e41]">{g.contact_count || 0} contacts</div>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* CSV Import Modal */}
      <Dialog open={importModal} onOpenChange={(o) => { setImportModal(o); if (!o) resetImport(); }}>
        <DialogContent className="bg-white border-slate-200 max-w-md">
          <DialogHeader><DialogTitle className="text-slate-900">Import Contacts from CSV</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            {!importResult ? (
              <>
                {/* File Drop Zone */}
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-xl p-6 cursor-pointer text-center transition-all ${csvFile ? "border-[#0c1e41] bg-[#eef1f8]" : "border-slate-300 hover:border-[#0c1e41]/40 hover:bg-slate-50"}`}
                  data-testid="csv-drop-zone">
                  <input ref={fileInputRef} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => setCsvFile(e.target.files?.[0] || null)} />
                  {csvFile ? (
                    <div className="flex items-center justify-center gap-3">
                      <div className="w-10 h-10 bg-[#0c1e41] rounded-lg flex items-center justify-center">
                        <FileText size={18} className="text-white" />
                      </div>
                      <div className="text-left">
                        <p className="text-sm font-semibold text-slate-900 truncate max-w-[200px]">{csvFile.name}</p>
                        <p className="text-xs text-slate-500">{(csvFile.size / 1024).toFixed(1)} KB</p>
                      </div>
                      <button onClick={(e) => { e.stopPropagation(); setCsvFile(null); if (fileInputRef.current) fileInputRef.current.value = ""; }} className="ml-auto text-slate-400 hover:text-red-500 transition-colors">
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <>
                      <Upload size={24} className="text-slate-400 mx-auto mb-2" />
                      <p className="text-sm font-medium text-slate-700">Click to upload CSV file</p>
                      <p className="text-xs text-slate-400 mt-1">Requires a <code className="bg-slate-100 px-1 rounded">phone</code> column. First Name, Last Name, Email, Company optional.</p>
                    </>
                  )}
                </div>

                {/* Group assignment */}
                <div className="space-y-2">
                  <Label className="text-slate-700 text-sm">Add to Group (optional)</Label>
                  <select value={csvGroupId} onChange={(e) => setCsvGroupId(e.target.value)}
                    className="w-full bg-white border border-slate-300 text-slate-900 text-sm rounded-md px-3 py-2 focus:outline-none focus:border-[#0c1e41]"
                    data-testid="csv-group-select">
                    <option value="">No group</option>
                    {groups.map((g) => <option key={g._id} value={g._id}>{g.name}</option>)}
                  </select>
                </div>

                {/* CSV format hint */}
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="text-xs font-semibold text-slate-700 mb-1">Expected CSV format:</p>
                  <code className="text-xs text-slate-500 font-mono">phone,first_name,last_name,email,company</code><br />
                  <code className="text-xs text-slate-400 font-mono">08012345678,John,Doe,john@email.com,Acme</code>
                </div>

                <Button onClick={importCsv} disabled={importing || !csvFile} className="w-full bg-[#0c1e41] hover:bg-[#091629]" data-testid="import-csv-submit">
                  {importing ? <><Loader2 size={14} className="animate-spin mr-2" />Importing...</> : <><Upload size={14} className="mr-2" />Import Contacts</>}
                </Button>
              </>
            ) : (
              /* Result screen */
              <div className="text-center space-y-4">
                <div className="w-14 h-14 bg-emerald-50 border border-emerald-200 rounded-full flex items-center justify-center mx-auto">
                  <CheckCircle size={28} className="text-emerald-600" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-slate-900">Import Complete</h3>
                  <p className="text-sm text-slate-500 mt-1">{importResult.message}</p>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-center">
                    <div className="text-xl font-extrabold text-emerald-700">{importResult.added}</div>
                    <div className="text-xs text-emerald-600">Added</div>
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center">
                    <div className="text-xl font-extrabold text-amber-700">{importResult.skipped}</div>
                    <div className="text-xs text-amber-600">Skipped</div>
                  </div>
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-center">
                    <div className="text-xl font-extrabold text-slate-700">{importResult.total}</div>
                    <div className="text-xs text-slate-600">Total</div>
                  </div>
                </div>
                {importResult.errors?.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-left">
                    <p className="text-xs font-semibold text-red-700 mb-1">Rows with errors:</p>
                    {importResult.errors.slice(0, 5).map((e, i) => <p key={i} className="text-xs text-red-600">{e}</p>)}
                  </div>
                )}
                <div className="flex gap-2">
                  <Button onClick={resetImport} variant="outline" className="flex-1 border-slate-300">Import More</Button>
                  <Button onClick={() => { setImportModal(false); resetImport(); }} className="flex-1 bg-[#0c1e41] hover:bg-[#091629]">Done</Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Contact Modal */}
      <Dialog open={contactModal} onOpenChange={setContactModal}>
        <DialogContent className="bg-white border-slate-200 max-w-md">
          <DialogHeader><DialogTitle className="text-slate-900">Add Contact</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 mt-2">
            {[["first_name","First Name *","John"],["last_name","Last Name","Doe"],["phone","Phone *","08012345678"],["email","Email","john@email.com"],["company","Company","Acme Corp"],["birthday","Birthday (YYYY-MM-DD)","1990-01-15"]].map(([k,l,p]) => (
              <div key={k} className={`space-y-1.5 ${k === "phone" || k === "email" ? "col-span-2" : ""}`}>
                <Label className="text-slate-700 text-xs">{l}</Label>
                <Input value={form[k] || ""} onChange={(e) => setForm({...form,[k]:e.target.value})} placeholder={p} className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 h-9 text-sm" />
              </div>
            ))}
          </div>
          <Button onClick={saveContact} disabled={saving} className="w-full bg-[#0c1e41] hover:bg-[#091629] mt-2">
            {saving ? "Adding..." : "Add Contact"}
          </Button>
        </DialogContent>
      </Dialog>

      {/* Add Group Modal */}
      <Dialog open={groupModal} onOpenChange={setGroupModal}>
        <DialogContent className="bg-white border-slate-200 max-w-sm">
          <DialogHeader><DialogTitle className="text-slate-900">Create Group</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="space-y-2"><Label className="text-slate-700 text-sm">Group Name *</Label><Input value={groupForm.name} onChange={(e) => setGroupForm({...groupForm,name:e.target.value})} placeholder="e.g. VIP Customers" className="bg-white border-slate-300 text-slate-900 h-10 placeholder:text-slate-400" /></div>
            <div className="space-y-2"><Label className="text-slate-700 text-sm">Description</Label><Input value={groupForm.description} onChange={(e) => setGroupForm({...groupForm,description:e.target.value})} placeholder="Optional description" className="bg-white border-slate-300 text-slate-900 h-10 placeholder:text-slate-400" /></div>
            <Button onClick={saveGroup} disabled={saving} className="w-full bg-[#0c1e41] hover:bg-[#091629]">Create Group</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
